import { firstValueFrom, of } from 'rxjs';
import { DataConversionCalculationDto } from '../models/data-conversion-calculation-dto.model';
import { DataConversionDto } from '../models/data-conversion-dto.model';
import { DataConversionUserSetting } from '../models/data-conversion-user-setting.model';
import { Precision } from '../models/precision.model';
import { ValueType } from '../models/value-type.model';
import { DataConversionBaseService } from '../services/data-conversion-base.service';
import { PrecisionService } from './precision.service';

type ConversionParameterType = {
  value: number;
  dataConversionDto: DataConversionDto;
  targetUnit: string;
  userSettingsDataConversions: DataConversionUserSetting[];
  tenantPrecision: Precision[];
  referenceUnit: string;
  allDataConversionList: DataConversionDto[];
};

type ConversionArrayValuesParameterType = {
  data: number[][];
  dataConversionDto: DataConversionDto;
  targetUnit: string;
  userSettingsDataConversions: DataConversionUserSetting[];
  tenantPrecision: Precision[];
  referenceUnit: string;
  allDataConversionList: DataConversionDto[];
};

type GetCalculationDtoParameters = {
  value: number | string;
  dataConversionDto: DataConversionDto;
  targetUnit: string;
  referenceUnit: string;
  allDataConversionList: DataConversionDto[];
  convertBack?: boolean;
};

export class DataConversionService extends DataConversionBaseService {
  private readonly callStack: Map<ValueType, Promise<any>> = new Map();

  constructor() {
    super();
  }

  async convertValue(conversionParameterType: ConversionParameterType): Promise<number | undefined> {
    const { value, dataConversionDto, targetUnit, userSettingsDataConversions, tenantPrecision, referenceUnit, allDataConversionList } =
      conversionParameterType;
    if (typeof value !== 'number') {
      return undefined;
    }
    const calculationDto = await this.validateAndGetCalculationDto({
      value,
      dataConversionDto,
      targetUnit,
      referenceUnit,
      allDataConversionList,
      convertBack: false,
    });
    const convertedValue = await this.convert<number>(value, false, calculationDto);
    return this.setPrecision(convertedValue, dataConversionDto, userSettingsDataConversions, tenantPrecision);
  }

  async convertLabel(
    label: string,
    dataConversionDto: DataConversionDto,
    targetUnit: string,
    referenceUnit: string,
    allDataConversionList: DataConversionDto[],
  ): Promise<string> {
    if (typeof label !== 'string') {
      return '';
    }
    const calculationDto = await this.validateAndGetCalculationDto({
      value: label,
      dataConversionDto,
      targetUnit,
      referenceUnit,
      allDataConversionList,
      convertBack: false,
    });
    return this.convert<string>(label, true, calculationDto);
  }

  async convertValueBack(
    value: number,
    dataConversionDto: DataConversionDto,
    targetUnit: string,
    referenceUnit: string,
    allDataConversionList: DataConversionDto[],
  ): Promise<number> {
    const calculationDto = await this.validateAndGetCalculationDto({
      value,
      dataConversionDto,
      targetUnit,
      referenceUnit,
      allDataConversionList,
      convertBack: true,
    });
    return this.convert<number>(value, false, calculationDto);
  }

  async convertArrayOfValues(conversionArrayValuesParameterType: ConversionArrayValuesParameterType): Promise<Array<Array<number>>> {
    const { data, dataConversionDto, targetUnit, userSettingsDataConversions, tenantPrecision, referenceUnit, allDataConversionList } =
      conversionArrayValuesParameterType;
    const convertedArrayOfValues: number[][] = [];

    for (const array of data) {
      const convertedArray: number[] = [];

      convertedArray.push(array[0]);
      for (let i = 1; i < array.length; i++) {
        const convertedValue = await this.convertValue({
          value: array[i],
          dataConversionDto,
          targetUnit,
          userSettingsDataConversions,
          tenantPrecision,
          referenceUnit,
          allDataConversionList,
        });

        if (convertedValue != undefined && Number.isFinite(convertedValue)) {
          convertedArray.push(convertedValue);
        }
      }

      convertedArrayOfValues.push(convertedArray);
    }

    return convertedArrayOfValues;
  }

  async validateAndGetCalculationDto(
    getCalculationDtoParameters: GetCalculationDtoParameters,
  ): Promise<DataConversionCalculationDto | undefined> {
    const { value, dataConversionDto, targetUnit, referenceUnit, allDataConversionList, convertBack } = getCalculationDtoParameters;
    if (!this.inputValid(value, dataConversionDto)) {
      return undefined;
    }

    return this.loadDataConversionCalculationDto(
      dataConversionDto.valueType,
      convertBack ? targetUnit : dataConversionDto.sourceUnit,
      convertBack ? dataConversionDto.sourceUnit : targetUnit,
      referenceUnit,
      allDataConversionList,
    );
  }

  async getAndValidateUserSettings(
    dataConversionDto: DataConversionDto,
    userSettingsDataConversion: DataConversionUserSetting[],
  ): Promise<DataConversionUserSetting | undefined> {
    if (this.checkIfValidDataConvList(dataConversionDto, userSettingsDataConversion)) {
      if (userSettingsDataConversion && userSettingsDataConversion?.length > 0) {
        const targetUnitObj = userSettingsDataConversion?.find(
          (conversion: DataConversionUserSetting) => conversion.valueType === dataConversionDto.valueType,
        );

        return targetUnitObj || undefined;
      } else {
        return undefined;
      }
    } else {
      return undefined;
    }
  }

  async loadDataConversionCalculationDto(
    valueType: ValueType,
    sourceUnit: string,
    targetUnit: string,
    referenceUnit: string,
    allDataConversionList: DataConversionDto[],
  ): Promise<DataConversionCalculationDto> {
    if (sourceUnit === targetUnit || `°${sourceUnit}` === targetUnit) {
      return {} as DataConversionCalculationDto;
    }

    const cachedData = this.getCacheItem(this.formatCacheKey(valueType, sourceUnit, targetUnit));
    if (cachedData) {
      return cachedData;
    } else {
      if (this.callStack.get(valueType)) {
        return this.callStack.get(valueType);
      } else {
        // Put call into callStack
        this.callStack.set(
          valueType,
          firstValueFrom(
            of(
              this.getCalculationDto(
                {
                  sourceUnit: sourceUnit,
                  targetUnit: targetUnit,
                  valueType: valueType,
                },
                referenceUnit,
                allDataConversionList,
              ),
            ),
          ),
        );
        // Await call and add item to cache
        const dataConversionCalculationDto = await this.callStack.get(valueType);

        // Set cache
        this.setCacheItem(dataConversionCalculationDto);

        // delete call from callStack
        this.callStack.delete(valueType);
        return dataConversionCalculationDto;
      }
    }
  }

  private async setPrecision(
    value: number,
    dataConversionDto: DataConversionDto,
    userSettingsDataConversions: DataConversionUserSetting[],
    tenantPrecision: Precision[],
  ): Promise<number> {
    const precision = await PrecisionService.prototype.convert({
      value,
      valueType: dataConversionDto?.valueType,
      sourceUnit: dataConversionDto?.sourceUnit,
      sensorPrecision: dataConversionDto?.sensorPrecision,
      userSettingsDataConversions,
      tenantPrecision,
    });

    return precision ?? value;
  }

  private inputValid(value: number | string, dataConversionDto: DataConversionDto): boolean {
    return !(this.isNullOrUndefined(value) || !dataConversionDto?.valueType || !dataConversionDto?.sourceUnit);
  }

  private checkIfValidDataConvList(
    dataConversionDto: DataConversionDto,
    userSettingsDataConversions: DataConversionUserSetting[],
  ): boolean {
    if (!userSettingsDataConversions) {
      return false;
    }

    const conversion = userSettingsDataConversions.find((c: DataConversionUserSetting) => c.valueType === dataConversionDto.valueType);

    return !!conversion?.targetUnit;
  }

  async getCalculationDto(
    dataConversionDto: DataConversionDto,
    referenceUnit: string,
    allDataConversionList: DataConversionDto[],
  ): Promise<DataConversionCalculationDto | undefined> {
    const toRefUnit = await this.findByUnits(
      {
        valueType: dataConversionDto.valueType,
        sourceUnit: dataConversionDto.sourceUnit,
        targetUnit: referenceUnit,
      },
      allDataConversionList,
    );

    const fromRefUnit = await this.findByUnits(
      {
        valueType: dataConversionDto.valueType,
        sourceUnit: referenceUnit,
        targetUnit: dataConversionDto.targetUnit,
      },
      allDataConversionList,
    );

    if (toRefUnit && !fromRefUnit) {
      return {
        toReferenceUnit: toRefUnit,
        fromReferenceUnit: undefined,
      };
    } else if (!toRefUnit && fromRefUnit) {
      return {
        toReferenceUnit: undefined,
        fromReferenceUnit: fromRefUnit,
      };
    } else if (!toRefUnit && !fromRefUnit) {
      return {
        toReferenceUnit: undefined,
        fromReferenceUnit: undefined,
      };
    } else if (toRefUnit && fromRefUnit) {
      return {
        toReferenceUnit: toRefUnit,
        fromReferenceUnit: fromRefUnit,
      };
    } else {
      return undefined;
    }
  }

  private async findByUnits(dataConversionDto: DataConversionDto, allDataConversionList: DataConversionDto[]) {
    return allDataConversionList.find((dataConversion: DataConversionDto) => {
      return (Object.keys(dataConversionDto) as Array<keyof DataConversionDto>).every((propertyName: keyof DataConversionDto) => {
        return dataConversion[propertyName] === dataConversionDto[propertyName];
      });
    });
  }
}
